import { useCallback, useRef, useSyncExternalStore } from "react";

export interface Store<S> {
  get(): S;
  set(update: Partial<S> | ((s: S) => Partial<S>)): void;
  subscribe(listener: () => void): () => void;
}

/** A tiny Zustand-like store. State is replaced immutably on each `set`. */
export function createStore<S extends object>(initial: S): Store<S> {
  let state = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => state,
    set(update) {
      const patch = typeof update === "function" ? update(state) : update;
      if (Object.keys(patch).length === 0) return;
      state = { ...state, ...patch };
      listeners.forEach((l) => l());
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => Object.is(v, b[i]));
  }
  if (a && b && typeof a === "object" && typeof b === "object") {
    const ka = Object.keys(a);
    const kb = Object.keys(b);
    if (ka.length !== kb.length) return false;
    return ka.every((k) => Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
  }
  return false;
}

/**
 * Subscribe to a derived slice. The selector result is compared shallowly so selectors
 * may return fresh arrays/objects (e.g. filtered lists) without re-render loops.
 */
export function useStoreSelector<S extends object, T>(store: Store<S>, selector: (s: S) => T): T {
  const cache = useRef<{ value: T } | null>(null);
  const selectorRef = useRef(selector);
  selectorRef.current = selector;
  const getSnapshot = useCallback(() => {
    const next = selectorRef.current(store.get());
    const prev = cache.current;
    if (prev && shallowEqual(prev.value, next)) return prev.value;
    cache.current = { value: next };
    return next;
  }, [store]);
  return useSyncExternalStore(store.subscribe, getSnapshot, getSnapshot);
}
